import axios from 'axios'

const endpoint = process.env.VUE_APP_AZURE_TRANSLATOR_ENDPOINT
const key = process.env.VUE_APP_AZURE_TRANSLATOR_KEY
const region = process.env.VUE_APP_AZURE_TRANSLATOR_REGION

function cacheKey(text: string, to: string): string {
	return `trans_${to}_${btoa(unescape(encodeURIComponent(text)))}`
}

function getCached(text: string, to: string): string | null {
	if (typeof localStorage === 'undefined') return null
	return localStorage.getItem(cacheKey(text, to))
}

function setCached(text: string, to: string, translated: string): void {
	if (typeof localStorage === 'undefined') return
	// localStorage.setItem(cacheKey(text, to), translated)
	try {
		localStorage.setItem(cacheKey(text, to), translated)
	} catch (e) {
		if (e instanceof DOMException && e.name === "QuotaExceededError") {
			console.error("儲存失敗，localStorage 空間已滿");
		} else {
			console.error("其他錯誤：", e);
		}
	}
}

export async function translateTexts(texts: string[], to: string): Promise<string[]> {
	const results: string[] = new Array(texts.length)
	const untranslated: { text: string; index: number }[] = []

	texts.forEach((t, i) => {
		if (!t) {
			results[i] = t
			return
		}
		const cached = getCached(t, to)
		if (cached) {
			results[i] = cached
		} else {
			untranslated.push({ text: t, index: i })
		}
	})

	if (untranslated.length > 0 && key) {
		const body = untranslated.map(u => ({ Text: u.text }))
		try {
			const resp = await axios.post(`${endpoint}/translate?api-version=3.0&to=${to}`,
				body,
				{
					headers: {
						'Ocp-Apim-Subscription-Key': key,
						'Ocp-Apim-Subscription-Region': region,
						'Content-Type': 'application/json'
					}
				})
			resp.data.forEach((item: any, idx: number) => {
				const translated = item.translations[0].text
				const { index, text } = untranslated[idx]
				results[index] = translated
				// setCached(text, to, translated)
			})
		} catch (error) {
				console.error('translate error', error)
				untranslated.forEach(u => {
				results[u.index] = texts[u.index]
			})
		}
	}

	return results
}

export async function translateMatches(matches: any[], to: string): Promise<void> {
	// 收集所有需要翻譯的文字和對應的 setter function
	const allTexts: string[] = []
	const allSetters: ((t: string) => void)[] = []

	// 遍歷所有 matches，收集需要翻譯的文字
	matches.forEach(match => {
		try {
			if (typeof match.query === 'string') {
				allTexts.push(match.query)
				allSetters.push(t => (match.query = t))
			}
			
			if (typeof match.content === 'string') {
				allTexts.push(match.content)
				allSetters.push(t => (match.content = t))
			}

			if (typeof match.location === 'string') {
				allTexts.push(match.location)
				allSetters.push(t => (match.location = t))
			}
			
			if (Array.isArray(match.summaries)) {
				match.summaries.forEach((s: string, idx: number) => {
					allTexts.push(s)
					allSetters.push(t => (match.summaries[idx] = t))
				})
			}

			if (match.unit) {
				if (Array.isArray(match.unit.entityIndustry)) {
					match.unit.entityIndustry.forEach((text, index) => {
						allTexts.push(text)
						allSetters.push(t => (match.unit.entityIndustry[index] = t))
					})
				}
				if (match.unit.tags?.chapter) {
					allTexts.push(match.unit.tags.chapter)
					allSetters.push(t => (match.unit.tags.chapter = t))
				}
				if (match.unit.tags?.region) {
					allTexts.push(match.unit.tags.region)
					allSetters.push(t => (match.unit.tags.region = t))
				}
				if (match.unit.tags?.type) {
					allTexts.push(match.unit.tags.type)
					allSetters.push(t => (match.unit.tags.type = t))
				}

				if (Array.isArray(match.unit.demands)) {
					match.unit.demands.forEach((d: any, idx: number) => {
						const txt = d.description || d.description_short || d.descriptionShort
						allTexts.push(txt)
						allSetters.push(t => {
							if (d.description !== undefined) d.description = t
							else if (d.description_short !== undefined) d.description_short = t
							else d.descriptionShort = t
						})
					})
				}
				if (Array.isArray(match.unit.supplies)) {
					match.unit.supplies.forEach((s: any, idx: number) => {
						const txt = s.description || s.descriptionShort || s.description_short
						allTexts.push(txt)
						allSetters.push(t => {
							if (s.description !== undefined) s.description = t
							else if (s.description_short !== undefined) s.description_short = t
							else s.descriptionShort = t
						})
					})
				}

				if (typeof match.unit.entityDescription === 'string') {
					allTexts.push(match.unit.entityDescription)
					allSetters.push(t => (match.unit.entityDescription = t))
				}
				if (typeof match.unit.entityDescriptionShort === 'string') {
					allTexts.push(match.unit.entityDescriptionShort)
					allSetters.push(t => (match.unit.entityDescriptionShort = t))
				}
			}
		} catch (err) {
			console.error('[收集翻譯文字失敗]', match, err)
		}
	})

	// 分批處理翻譯，每批最多 400 個元素（Azure Translator API 限制）
	if (allTexts.length > 0) {
		const BATCH_SIZE = 400
		const batches: {
			texts: string[]
			setters: ((t: string) => void)[]
			startIndex: number
		}[] = []
		
		// 將文字和 setters 分批
		for (let i = 0; i < allTexts.length; i += BATCH_SIZE) {
			batches.push({
				texts: allTexts.slice(i, i + BATCH_SIZE),
				setters: allSetters.slice(i, i + BATCH_SIZE),
				startIndex: i
			})
		}

		// 同步處理所有批次
		const batchPromises = batches.map(async (batch) => {
			try {
				const translated = await translateTexts(batch.texts, to)
				// 將翻譯結果套用回原始資料
				translated.forEach((t, i) => batch.setters[i](t))
			} catch (err) {
				console.error(`[批次翻譯失敗 - 批次起始索引: ${batch.startIndex}]`, err)
			}
		})

		// 等待所有批次完成
		await Promise.all(batchPromises)
	}
}

export async function translateMatch(match: any, to: string): Promise<void> {
	const texts: string[] = []
	const setters: ((t: string) => void)[] = []

	if (Array.isArray(match.summaries)) {
		match.summaries.forEach((s: string, idx: number) => {
			texts.push(s)
			setters.push(t => (match.summaries[idx] = t))
		})
	}

	if (typeof match.location === 'string') {
		texts.push(match.location)
		setters.push(t => (match.location = t))
	}

	if (match.unit) {
		if (Array.isArray(match.unit.entityIndustry)) {
			match.unit.entityIndustry.forEach((text: string, index: number) => {
				texts.push(text)
				setters.push(t => (match.unit.entityIndustry[index] = t))
			})
		}
		if (match.unit.tags?.chapter) {
			texts.push(match.unit.tags.chapter)
			setters.push(t => (match.unit.tags.chapter = t))
		}
		if (match.unit.tags?.region) {
			texts.push(match.unit.tags.region)
			setters.push(t => (match.unit.tags.region = t))
		}
		if (match.unit.tags?.type) {
			texts.push(match.unit.tags.type)
			setters.push(t => (match.unit.tags.type = t))
		}

		if (Array.isArray(match.unit.demands)) {
			match.unit.demands.forEach((d: any, idx: number) => {
				const txt = d.description || d.description_short || d.descriptionShort
				texts.push(txt)
				setters.push(t => {
					if (d.description !== undefined) d.description = t
					else if (d.description_short !== undefined) d.description_short = t
					else d.descriptionShort = t
				})
			})
		}
		if (Array.isArray(match.unit.supplies)) {
			match.unit.supplies.forEach((s: any, idx: number) => {
				const txt = s.description || s.descriptionShort || s.description_short
				texts.push(txt)
				setters.push(t => {
					if (s.description !== undefined) s.description = t
					else if (s.description_short !== undefined) s.description_short = t
					else s.descriptionShort = t
				})
			})
		}

		if (typeof match.unit.entityDescription === 'string') {
			texts.push(match.unit.entityDescription)
			setters.push(t => (match.unit.entityDescription = t))
		}
		if (typeof match.unit.entityDescriptionShort === 'string') {
			texts.push(match.unit.entityDescriptionShort)
			setters.push(t => (match.unit.entityDescriptionShort = t))
		}
	}

	const translated = await translateTexts(texts, to)
	translated.forEach((t, i) => setters[i](t))
}


export async function translateUser(user: any, to: string): Promise<void> {
	const texts: string[] = []
	const setters: ((t: string) => void)[] = []

	if (Array.isArray(user.companyIndustry)) {
		user.companyIndustry.forEach((text, index) => {
			texts.push(text)
			setters.push(t => (user.companyIndustry[index] = t))
		})
	}

	if (Array.isArray(user.demands)) {
		user.demands.forEach((d: any, idx: number) => {
			['description_short', 'descriptionShort','description'].forEach(key => {
				if (typeof d[key] === 'string') {
					texts.push(d[key])
					setters.push(t => (d[key] = t))
				}
			})
		})
	}
	if (Array.isArray(user.supplies)) {
		user.supplies.forEach((s: any, idx: number) => {
			['description_short', 'descriptionShort', 'description'].forEach(key => {
				if (typeof s[key] === 'string') {
					texts.push(s[key])
					setters.push(t => (s[key] = t))
				}
			})
		})
	}
	
	const translated = await translateTexts(texts, to)
	translated.forEach((t, i) => setters[i](t))
}

export async function translateRecordValues(record: Record<string, string>, to: string): Promise<void> {
	const keys = Object.keys(record)
	const values = Object.values(record)
  
	if (values.length === 0) return
  
	try {
		const translatedValues = await translateTexts(values, to)
		keys.forEach((key, index) => {
		record[key] = translatedValues[index]
		})
	} catch (err) {
		console.error('[translateRecordValuesInPlace error]', err)
	}
}

export async function translateMatchRequest(matchRequest: any, to: string): Promise<void> {
	const texts: string[] = []
	const setters: ((t: string) => void)[] = []

	// 翻譯 aiResponse
	if (typeof matchRequest.aiResponse === 'string') {
		texts.push(matchRequest.aiResponse)
		setters.push(t => (matchRequest.aiResponse = t))
	}

	// 翻譯 simplifiedDemands
	if (Array.isArray(matchRequest.simplifiedDemands)) {
		matchRequest.simplifiedDemands.forEach((demand: any) => {
			if (typeof demand.categoryTitle === 'string') {
				texts.push(demand.categoryTitle)
				setters.push(t => (demand.categoryTitle = t))
			}
			if (typeof demand.description === 'string') {
				texts.push(demand.description)
				setters.push(t => (demand.description = t))
			}
			if (typeof demand.reason === 'string') {
				texts.push(demand.reason)
				setters.push(t => (demand.reason = t))
			}
			if (typeof demand.originalQuery === 'string') {
				texts.push(demand.originalQuery)
				setters.push(t => (demand.originalQuery = t))
			}
		})
	}

	// 翻譯 simplifiedSupplies
	if (Array.isArray(matchRequest.simplifiedSupplies)) {
		matchRequest.simplifiedSupplies.forEach((supply: any) => {
			if (typeof supply.categoryTitle === 'string') {
				texts.push(supply.categoryTitle)
				setters.push(t => (supply.categoryTitle = t))
			}
			if (typeof supply.description === 'string') {
				texts.push(supply.description)
				setters.push(t => (supply.description = t))
			}
			if (typeof supply.reason === 'string') {
				texts.push(supply.reason)
				setters.push(t => (supply.reason = t))
			}
			if (typeof supply.originalQuery === 'string') {
				texts.push(supply.originalQuery)
				setters.push(t => (supply.originalQuery = t))
			}
		})
	}

	const translated = await translateTexts(texts, to)
	translated.forEach((t, i) => setters[i](t))
}
